// src/pages/HeadlinesSection.tsx -- Slice K / Phase 3.
//
// Headlines App home-page surface. Two components live here:
//   * HeadlinesSection -- fetches /api/headlines on mount, renders 0..2
//                         cards stacked (or side-by-side ≥900px). The
//                         "+ Post headline" button surfaces for users
//                         with `headlines.post`. Posting UI ships in the
//                         next commit; the button is a no-op stub here.
//   * HeadlineCard     -- one card. Royal-green + gold visual, deliberately
//                         independent of the user-selected theme skin
//                         (P3.9 -- headlines carry their own brand).
//
// CSS custom properties (`--c-headline-*`) are inlined on the card root so
// the useTheme hook can never override them.

import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetch, ApiError } from "../api";
import { useCurrentUser, type MeResponse } from "../auth";
import { C, F } from "../theme";
import { Button, Card, Eyebrow, Field } from "../components/ui";
import { canPostHeadlines } from "../lib/permissions";

// Royal green + gold heritage palette -- locked, theme-independent.
const HEADLINE_BG = "#0F4D2A";
const HEADLINE_ACCENT = "#C9A24B";
const HEADLINE_INK = "#FFFFFF";
const HEADLINE_INK_MUTE = "rgba(255,255,255,0.72)";

export type VoteValue = "up" | "down" | null;

export interface Headline {
  id: number;
  title: string;
  body: string;
  image_url: string | null;
  author_name: string;
  posted_by: number;
  posted_at: string;
  expires_at: string;
  /** Slice L / P7: always present. The caller's own vote. */
  my_vote: VoteValue;
  /** Slice L / P7: present ONLY when caller is the author or has
   *  headlines.post. Absent for everyone else by API design -- never
   *  render `0` as a fallback or you'll undo the privacy gate. */
  up_count?: number;
  down_count?: number;
}

interface HeadlinesResponse {
  headlines: Headline[];
  canPost: boolean;
}

// ---------------------------------------------------------------------------
// HeadlinesSection
// ---------------------------------------------------------------------------

export function HeadlinesSection() {
  const { me } = useCurrentUser();
  const [headlines, setHeadlines] = useState<Headline[]>([]);
  const [loading, setLoading] = useState(true);
  const [postingOpen, setPostingOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const data = await apiFetch<HeadlinesResponse>("/api/headlines");
      setHeadlines(data.headlines);
    } catch {
      // Headlines is a non-critical surface -- silent fail keeps the
      // home page rendering when the API hiccups.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  function setVote(id: number, my_vote: VoteValue) {
    // Slice L / P7: when the caller can see counts (author or admin), nudge
    // them optimistically so the UI doesn't lag the network. Counts are
    // absent for everyone else, so the conditional checks avoid emitting 0.
    setHeadlines((cur) =>
      cur.map((h) => {
        if (h.id !== id) return h;
        const next: Headline = { ...h, my_vote };
        if (typeof h.up_count === "number" && typeof h.down_count === "number") {
          const prev = h.my_vote;
          let up = h.up_count;
          let down = h.down_count;
          if (prev === "up") up = Math.max(0, up - 1);
          if (prev === "down") down = Math.max(0, down - 1);
          if (my_vote === "up") up += 1;
          if (my_vote === "down") down += 1;
          next.up_count = up;
          next.down_count = down;
        }
        return next;
      }),
    );
  }

  function removeOne(id: number) {
    setHeadlines((cur) => cur.filter((h) => h.id !== id));
  }

  const canPost = canPostHeadlines(me);
  const atMaxActive = headlines.length >= 2;
  const showSection = headlines.length > 0 || canPost;

  if (loading || !showSection) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {canPost && (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={() => setPostingOpen(true)}
            disabled={atMaxActive}
            title={atMaxActive ? "Max 2 active headlines -- wait for one to expire or delete one." : undefined}
            style={{
              padding: "6px 12px",
              borderRadius: 999,
              border: `1px solid ${HEADLINE_ACCENT}`,
              background: HEADLINE_BG,
              color: HEADLINE_ACCENT,
              fontFamily: F.body,
              fontWeight: 800,
              fontSize: 12,
              letterSpacing: 0.4,
              cursor: atMaxActive ? "not-allowed" : "pointer",
              opacity: atMaxActive ? 0.5 : 1,
            }}
          >
            + Post headline
          </button>
        </div>
      )}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: headlines.length >= 2 ? "repeat(auto-fit, minmax(280px, 1fr))" : "1fr",
          gap: 10,
        }}
      >
        {headlines.map((h) => (
          <HeadlineCard
            key={h.id}
            headline={h}
            me={me}
            onVoteChanged={setVote}
            onDeleted={removeOne}
          />
        ))}
      </div>
      {postingOpen && (
        <PostHeadlineModal
          me={me}
          onClose={() => setPostingOpen(false)}
          onPosted={() => { setPostingOpen(false); refresh(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PostHeadlineModal
// ---------------------------------------------------------------------------

const MAX_TITLE = 100;
const MAX_BODY = 100;

function PostHeadlineModal({
  me, onClose, onPosted,
}: {
  me: MeResponse | null;
  onClose: () => void;
  onPosted: () => void;
}) {
  const defaultAuthor = me ? `${me.first ?? ""} ${me.last ?? ""}`.trim() : "";
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [author, setAuthor] = useState(defaultAuthor || me?.upn || "");
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const titleTrim = title.trim();
  const bodyTrim = body.trim();
  const authorTrim = author.trim();
  const canSubmit =
    !posting &&
    titleTrim.length >= 3 && titleTrim.length <= MAX_TITLE &&
    bodyTrim.length >= 3 && bodyTrim.length <= MAX_BODY &&
    authorTrim.length > 0;

  async function chooseImage(file: File) {
    setUploading(true);
    setError(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch("/api/newsletter/upload-image", {
        method: "POST",
        body: form,
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: "Upload failed" }));
        throw new ApiError(res.status, body, (body as { error?: string }).error ?? `Upload failed (${res.status})`);
      }
      const { blob_url } = (await res.json()) as { blob_url: string };
      setImageUrl(blob_url);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Image upload failed.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function submit() {
    if (!canSubmit) return;
    setPosting(true);
    setError(null);
    try {
      await apiFetch("/api/headlines", {
        method: "POST",
        body: JSON.stringify({
          title: titleTrim,
          body: bodyTrim,
          image_url: imageUrl,
          author_name: authorTrim,
        }),
      });
      onPosted();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not post headline.");
      setPosting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="post-headline-title"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(43,32,20,0.55)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
        zIndex: 1000,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "8vh 16px",
        overflowY: "auto",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 520,
          background: C.card,
          borderRadius: 18,
          boxShadow: C.shadowL,
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${C.hairlineLt}`, background: C.paperSoft }}>
          <Eyebrow>Post headline</Eyebrow>
          <h2 id="post-headline-title" style={{ fontFamily: F.serif, fontSize: 20, fontWeight: 700, color: C.ink, margin: "2px 0 0" }}>
            New headline
          </h2>
        </div>

        <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 4 }}>
          <Field
            label={`Title (3-${MAX_TITLE})`}
            value={title}
            onChange={setTitle}
            max={MAX_TITLE}
            ph="Charter trains tomorrow!"
          />
          <Field
            label={`Body (3-${MAX_BODY})`}
            value={body}
            onChange={setBody}
            rows={3}
            max={MAX_BODY}
            ph="Short and timely. Expires after 15 days."
          />
          <Field
            label="Author"
            value={author}
            onChange={setAuthor}
            max={120}
            ph="Your name"
          />

          <div style={{ marginBottom: 4 }}>
            <Eyebrow>Image (optional)</Eyebrow>
            <Card padded={false} style={{ marginTop: 6, overflow: "hidden" }}>
              {imageUrl ? (
                <div>
                  <div
                    style={{
                      width: "100%",
                      height: 140,
                      backgroundImage: `url(${imageUrl})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  />
                  <div style={{ display: "flex", gap: 8, padding: 10 }}>
                    <Button variant="secondary" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                      Replace
                    </Button>
                    <Button variant="ghost" onClick={() => setImageUrl(null)} disabled={uploading}>
                      Remove
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  style={{
                    width: "100%",
                    height: 100,
                    background: C.paperWarm,
                    border: `1px dashed ${C.brassLt}`,
                    cursor: uploading ? "wait" : "pointer",
                    fontFamily: F.serif,
                    fontStyle: "italic",
                    fontSize: 13,
                    color: C.brassDk,
                  }}
                >
                  {uploading ? "Uploading…" : "+ Add image (PNG / JPG / WebP)"}
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) chooseImage(file);
                }}
              />
            </Card>
          </div>
        </div>

        <div style={{ padding: "12px 18px", borderTop: `1px solid ${C.hairlineLt}`, background: C.paperSoft, display: "flex", justifyContent: "flex-end", gap: 8, alignItems: "center" }}>
          {error && (
            <div style={{ fontFamily: F.body, fontSize: 12, fontWeight: 700, color: C.signalDk, marginRight: "auto" }}>
              {error}
            </div>
          )}
          <Button variant="secondary" onClick={onClose} disabled={posting}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={!canSubmit}>
            {posting ? "Posting…" : "Post"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HeadlineCard
// ---------------------------------------------------------------------------

function formatPostedAt(iso: string): string {
  const d = new Date(iso);
  const now = Date.now();
  const diffMin = Math.floor((now - d.getTime()) / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

const ANON_TOOLTIP = "Your vote is anonymous. Authors and management cannot see how you voted.";

function HeadlineCard({
  headline, me, onVoteChanged, onDeleted,
}: {
  headline: Headline;
  me: MeResponse | null;
  onVoteChanged: (id: number, my_vote: VoteValue) => void;
  onDeleted: (id: number) => void;
}) {
  const [working, setWorking] = useState<"vote" | "delete" | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const canDelete = me ? (me.isAdmin || me.hubUserId === headline.posted_by) : false;
  const showCounts =
    typeof headline.up_count === "number" && typeof headline.down_count === "number";

  async function vote(target: VoteValue) {
    if (working) return;
    setWorking("vote");
    // Compute the desired next state from the click target + current vote:
    //   tap-current  -> clear
    //   tap-opposite -> switch
    //   tap-unvoted  -> set
    const next: VoteValue = headline.my_vote === target ? null : target;
    try {
      const res = await apiFetch<{ my_vote: VoteValue }>(
        `/api/headlines/${headline.id}/vote`,
        { method: "POST", body: JSON.stringify({ vote: next }) },
      );
      onVoteChanged(headline.id, res.my_vote);
    } catch {
      /* silent -- vote flips are idempotent on retry */
    } finally {
      setWorking(null);
    }
  }

  async function deleteHeadline() {
    if (working) return;
    setWorking("delete");
    try {
      await apiFetch(`/api/headlines/${headline.id}`, { method: "DELETE" });
      onDeleted(headline.id);
    } catch (e) {
      setWorking(null);
      setConfirmDelete(false);
      if (e instanceof ApiError) {
        /* ignore -- caller's parent stays mounted */
      }
    }
  }

  return (
    <div
      style={{
        background: HEADLINE_BG,
        color: HEADLINE_INK,
        borderRadius: 14,
        padding: 14,
        border: `1px solid ${HEADLINE_ACCENT}55`,
        boxShadow: "0 6px 18px rgba(15,77,42,0.20), inset 0 1px 0 rgba(255,255,255,0.06)",
        display: "flex",
        gap: 14,
        alignItems: "flex-start",
        minHeight: 96,
        // Card root pins the headline-only CSS vars in case a child relies
        // on them (none do today, but future child components might).
        // These are intentionally NOT participating in useTheme.
        ...({
          "--c-headline-bg":     HEADLINE_BG,
          "--c-headline-accent": HEADLINE_ACCENT,
          "--c-headline-ink":    HEADLINE_INK,
        } as React.CSSProperties),
      }}
    >
      {/* Thumbnail */}
      <div
        style={{
          width: 80,
          height: 80,
          borderRadius: 10,
          flexShrink: 0,
          background: headline.image_url
            ? `url(${headline.image_url})`
            : `linear-gradient(135deg, ${HEADLINE_ACCENT}, #8A6A2E)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          border: `1px solid ${HEADLINE_ACCENT}66`,
        }}
        aria-hidden="true"
      />

      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 6 }}>
        <div
          style={{
            fontFamily: F.serif,
            fontSize: 17,
            fontWeight: 700,
            lineHeight: 1.2,
            letterSpacing: -0.2,
            color: HEADLINE_INK,
            wordBreak: "break-word",
          }}
        >
          {headline.title}
        </div>
        <div
          style={{
            fontFamily: F.body,
            fontSize: 13,
            fontWeight: 500,
            lineHeight: 1.4,
            color: HEADLINE_INK_MUTE,
            wordBreak: "break-word",
          }}
        >
          {headline.body}
        </div>

        {/* Footer */}
        <div
          style={{
            marginTop: "auto",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 10,
            paddingTop: 6,
            borderTop: `1px dashed ${HEADLINE_ACCENT}55`,
          }}
        >
          <div
            style={{
              fontFamily: F.body,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.4,
              color: HEADLINE_ACCENT,
              textTransform: "uppercase",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              minWidth: 0,
            }}
          >
            {headline.author_name} · {formatPostedAt(headline.posted_at)}
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {canDelete && (
              confirmDelete ? (
                <span style={{ display: "inline-flex", gap: 4 }}>
                  <button
                    type="button"
                    onClick={deleteHeadline}
                    disabled={working === "delete"}
                    style={{
                      background: "rgba(255,255,255,0.10)",
                      color: HEADLINE_ACCENT,
                      border: `1px solid ${HEADLINE_ACCENT}`,
                      borderRadius: 999,
                      padding: "3px 9px",
                      fontFamily: F.body,
                      fontSize: 11,
                      fontWeight: 800,
                      cursor: "pointer",
                    }}
                  >
                    Delete?
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(false)}
                    style={{
                      background: "transparent",
                      color: HEADLINE_INK_MUTE,
                      border: "none",
                      padding: "3px 6px",
                      fontFamily: F.body,
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  aria-label="Delete headline"
                  style={{
                    background: "transparent",
                    color: HEADLINE_INK_MUTE,
                    border: "none",
                    padding: 4,
                    fontSize: 14,
                    lineHeight: 1,
                    cursor: "pointer",
                  }}
                >
                  ×
                </button>
              )
            )}
            <ThumbButton
              direction="up"
              active={headline.my_vote === "up"}
              busy={working === "vote"}
              count={showCounts ? headline.up_count : undefined}
              onClick={() => vote("up")}
            />
            <ThumbButton
              direction="down"
              active={headline.my_vote === "down"}
              busy={working === "vote"}
              count={showCounts ? headline.down_count : undefined}
              onClick={() => vote("down")}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ThumbButton -- two-state vote control (Slice L / P7)
//
// Filled when active, outlined otherwise. Tooltip says the vote is anonymous;
// `title` covers desktop hover and mobile long-press (browser default).
// `count` is only shown when the API actually returns one -- never render `0`
// as a fallback or it breaks the privacy contract for non-authors.
// ---------------------------------------------------------------------------

function ThumbButton({
  direction, active, busy, count, onClick,
}: {
  direction: "up" | "down";
  active: boolean;
  busy: boolean;
  count: number | undefined;
  onClick: () => void;
}) {
  const ariaLabel =
    direction === "up"
      ? (active ? "Remove thumbs up" : "Thumbs up")
      : (active ? "Remove thumbs down" : "Thumbs down");

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      aria-pressed={active}
      aria-label={ariaLabel}
      title={ANON_TOOLTIP}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "4px 10px",
        borderRadius: 999,
        background: active ? HEADLINE_ACCENT : "rgba(255,255,255,0.08)",
        color: active ? HEADLINE_BG : HEADLINE_INK,
        border: `1px solid ${HEADLINE_ACCENT}`,
        fontFamily: F.body,
        fontSize: 12,
        fontWeight: 800,
        cursor: busy ? "wait" : "pointer",
      }}
    >
      <ThumbIcon direction={direction} filled={active} size={14} />
      {typeof count === "number" && <span>{count}</span>}
    </button>
  );
}

function ThumbIcon({
  direction, filled, size,
}: {
  direction: "up" | "down";
  filled: boolean;
  size: number;
}) {
  // Lucide ThumbsUp path; ThumbsDown is the same rotated 180°.
  const path = "M7 10v12H3V10zM7 10l5-7a3 3 0 0 1 3 3v4h5a2 2 0 0 1 2 2.4l-1.5 7A2 2 0 0 1 18.5 21H7";
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={direction === "down" ? { transform: "rotate(180deg)" } : undefined}
      aria-hidden="true"
    >
      <path d={path} />
    </svg>
  );
}
